import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { aiAgentService } from "@/services/breeze/AIAgentService";
import { digitalOceanChatService } from "@/services/breeze/DigitalOceanChatService";
import {
  chatPersistenceService,
  ChatConversation,
} from "@/services/breeze/ChatPersistenceService";
import { DigitalOceanAgent } from "@/types/breeze/AIAgent";
import { useAuth } from "@/providers/AuthProvider";
import toast from "react-hot-toast";
import { DEFAULT_CHAT_MODEL } from "@/lib/anythinkChatTransport";

export interface Message {
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: Date;
}

export interface Conversation {
  id: number;
  name: string;
  messages: Message[];
  created_at: Date;
  agentConversationId?: string;
  message_count?: number;
}

export type ChatMode = "agent" | "inference";

export interface UseAgentChatProps {
  isEnabled?: boolean;
  initialAgentId?: string;
}

export const useAgentChat = ({
  isEnabled = true,
  initialAgentId,
}: UseAgentChatProps = {}) => {
  const { currentUser, currentTenant } = useAuth();
  const [selectedAgent, setSelectedAgent] = useState<DigitalOceanAgent | null>(
    null,
  );
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<
    number | null
  >(null);
  const [messageInput, setMessageInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatMode, setChatMode] = useState<ChatMode>("agent");
  const [selectedModel, setSelectedModel] = useState(DEFAULT_CHAT_MODEL);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: agentsResponse } = useQuery({
    queryKey: ["ai-agents", "digitalocean"],
    queryFn: () => aiAgentService.getDigitalOceanAgents(),
    enabled: isEnabled && !!currentUser && !!currentTenant,
    retry: 2,
    retryDelay: 2000,
    staleTime: 5 * 60 * 1000,
  });

  const { data: availableModels } = useQuery({
    queryKey: ["digitalocean-models"],
    queryFn: () => digitalOceanChatService.getAvailableModels(),
    staleTime: 5 * 60 * 1000,
    enabled: isEnabled && !!currentUser && !!currentTenant,
  });

  const agents = agentsResponse?.agents || [];
  const activeConversation = conversations.find(
    (c) => c.id === activeConversationId,
  );

  // Auto-select first agent when available
  useEffect(() => {
    if (agents.length > 0 && !selectedAgent) {
      const agent = initialAgentId
        ? agents.find((a: DigitalOceanAgent) => a.id === initialAgentId) ||
          agents[0]
        : agents[0];
      setSelectedAgent(agent);
    }
  }, [agents, selectedAgent, initialAgentId]);

  // Load conversations when agent changes
  useEffect(() => {
    if (selectedAgent && currentUser && currentTenant) {
      loadConversations();
    }
  }, [selectedAgent, currentUser, currentTenant]);

  // Refresh conversations when the hook is enabled (e.g., when navigating back to the page)
  useEffect(() => {
    if (isEnabled && selectedAgent && currentUser && currentTenant) {
      loadConversations();
    }
  }, [isEnabled, selectedAgent, currentUser, currentTenant]);

  const loadConversations = async () => {
    if (!selectedAgent) return;

    try {
      const dbConversations = await chatPersistenceService.getConversations(
        selectedAgent.id,
      );
      const localConversations = dbConversations.map(
        (conv: ChatConversation) => ({
          id: conv.id,
          name: conv.name,
          messages: [],
          created_at: new Date(conv.created_at),
          agentConversationId: conv.metadata?.agentConversationId as string,
          message_count: conv.message_count || 0,
        }),
      );
      setConversations(localConversations);

      // Auto-select first conversation or create one if none exist
      if (localConversations.length > 0) {
        setActiveConversationId(localConversations[0].id);
      } else {
        createNewConversation();
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
      setConversations([]);
    }
  };

  const loadConversationMessages = async (conversationId: number) => {
    try {
      const conversation =
        await chatPersistenceService.getConversation(conversationId);
      const messages = (conversation.messages || []).map((msg) => ({
        id: msg.id.toString(),
        content: msg.content,
        role: msg.role as "user" | "assistant",
        timestamp: new Date(msg.created_at),
      }));

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, messages } : conv,
        ),
      );
    } catch (error) {
      console.error("Failed to load conversation messages:", error);
    }
  };

  // Load messages when active conversation changes
  useEffect(() => {
    if (activeConversationId) {
      loadConversationMessages(activeConversationId);
    }
  }, [activeConversationId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [activeConversation?.messages]);

  const createNewConversation = async () => {
    if (!selectedAgent) return;

    console.log(
      "Creating conversation with agent:",
      selectedAgent.name,
      "ID:",
      selectedAgent.id,
    );

    try {
      const newConversation = await chatPersistenceService.createConversation({
        name: `Chat with ${selectedAgent.name}`,
        agentId: selectedAgent.id,
      });

      const localConv = {
        id: newConversation.id,
        name: newConversation.name,
        messages: [],
        created_at: new Date(newConversation.created_at),
        agentConversationId: newConversation.metadata
          ?.agentConversationId as string,
        message_count: 0,
      };

      setConversations((prev) => [localConv, ...prev]);
      setActiveConversationId(newConversation.id);
      toast.success("New conversation created");
    } catch (error) {
      console.error("Failed to create conversation:", error);
      toast.error("Failed to create conversation");
    }
  };

  const switchAgent = (newAgent: DigitalOceanAgent) => {
    setSelectedAgent(newAgent);
    setActiveConversationId(null);
    // Don't clear conversations - let loadConversations handle loading existing ones
    // setConversations([]);
  };

  const sendMessage = async () => {
    if (
      !messageInput.trim() ||
      !selectedAgent ||
      isLoading ||
      !activeConversationId
    )
      return;

    const userMessageContent = messageInput.trim();
    setMessageInput("");
    setIsLoading(true);

    // Add user message to UI immediately for better UX
    const userMessage: Message = {
      id: Date.now().toString(),
      content: userMessageContent,
      role: "user",
      timestamp: new Date(),
    };

    setConversations((prev) =>
      prev.map((conv) =>
        conv.id === activeConversationId
          ? { ...conv, messages: [...conv.messages, userMessage] }
          : conv,
      ),
    );

    try {
      let response: string;

      if (chatMode === "agent") {
        // Backend now handles persistence, so we pass the conversationId
        const agentResponse = await digitalOceanChatService.sendAgentMessage(
          selectedAgent.id,
          userMessageContent,
          activeConversationId, // Pass the conversation ID to backend
        );
        response = agentResponse.response;
      } else {
        const conversationHistory =
          activeConversation?.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
          })) || [];

        response = await digitalOceanChatService.sendInferenceMessage(
          [
            ...conversationHistory,
            { role: "user", content: userMessageContent },
          ],
          selectedModel,
        );

        // For inference mode, we still need to save messages manually since backend doesn't handle it
        await chatPersistenceService.createMessage(activeConversationId, {
          content: userMessageContent,
          role: "user",
        });
      }

      // Add assistant response to UI immediately for better UX
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: response,
        role: "assistant",
        timestamp: new Date(),
      };

      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === activeConversationId
            ? { ...conv, messages: [...conv.messages, assistantMessage] }
            : conv,
        ),
      );

      // For inference mode, save assistant response manually
      if (chatMode === "inference") {
        await chatPersistenceService.createMessage(activeConversationId, {
          content: response,
          role: "assistant",
        });
      }

      // Note: No need to reload from database - backend handles persistence
      // and UI already shows the messages. Reloading causes messages to disappear.
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return {
    selectedAgent,
    conversations,
    activeConversationId,
    setActiveConversationId,
    messageInput,
    setMessageInput,
    isLoading,
    chatMode,
    setChatMode,
    selectedModel,
    setSelectedModel,
    messagesEndRef,
    agents,
    availableModels,
    activeConversation,
    createNewConversation,
    switchAgent,
    sendMessage,
    handleKeyPress,
    refreshConversations: loadConversations,
  };
};
